import { type ReactNode } from "react";
import DragOverlayProject from "./drag-overlay-project";

/**
 * DragOverlay用のプロジェクトレンダリングに必要な共通ロジック
 */

/**
 * DOMから現在のプロジェクトの開閉状態を取得する
 * @param projectId プロジェクトID
 * @returns 開閉状態
 */
function getProjectOpenState(projectId: string): boolean {
  // DOMから現在の開閉状態を取得（invisible状態でも取得可能なように改善）
  const projectElement = document.querySelector(`[data-id="${projectId}"]`);
  const collapsibleElement = projectElement?.querySelector("[data-state]");

  // invisible状態でもdata-state属性は保持されるため、確実に取得する
  const domOpenState =
    collapsibleElement?.getAttribute("data-state") === "open";

  // DOMクエリが失敗した場合は閉じているとみなす
  const isOpen = domOpenState ?? false;

  return isOpen;
}

/**
 * プロジェクトのパスパターンを生成
 * @param workspaceId ワークスペースID
 * @param projectType プロジェクトタイプ (ua/sa/iw)
 * @param projectId プロジェクトID
 * @returns URLパスパターン
 */
function createProjectPathPattern(
  workspaceId: string,
  projectType: "ua" | "sa" | "iw" | "ads",
  projectId: string,
  lang?: string,
): string {
  if (projectType === "ads" && lang) {
    return `.*/${encodeURIComponent(lang)}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(projectId)}/(board|list)`;
  }
  return `.*/${encodeURIComponent(workspaceId)}/${projectType}/${encodeURIComponent(projectId)}/(board|list)`;
}

/**
 * プロジェクトのリンクパスを生成
 * @param workspaceId ワークスペースID
 * @param projectType プロジェクトタイプ (ua/sa/iw/ads)
 * @param projectId プロジェクトID
 * @returns リンクパス
 */
function createProjectLinkPath(
  workspaceId: string,
  projectType: "ua" | "sa" | "iw" | "ads",
  projectId: string,
  lang?: string,
): string {
  if (projectType === "ads" && lang) {
    return `/${encodeURIComponent(lang)}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(projectId)}`;
  }
  return `/${encodeURIComponent(workspaceId)}/${projectType}/${encodeURIComponent(projectId)}`;
}

interface CreateDragOverlayProjectParams {
  projectId: string;
  projectName: string;
  projectColor: string;
  projectType: "ua" | "sa" | "iw" | "ads";
  workspaceId: string;
  children: ReactNode;
  lang?: string;
}

/**
 * DragOverlay用のプロジェクトコンポーネントを生成
 */
export function createDragOverlayProject({
  projectId,
  projectName,
  projectColor,
  projectType,
  workspaceId,
  children,
  lang,
}: CreateDragOverlayProjectParams): ReactNode {
  const pathPattern = createProjectPathPattern(
    workspaceId,
    projectType,
    projectId,
    lang,
  );
  const linkPath = createProjectLinkPath(
    workspaceId,
    projectType,
    projectId,
    lang,
  );
  const isOpen = getProjectOpenState(projectId);

  return (
    <DragOverlayProject
      projectName={projectName}
      projectColor={projectColor || "#000000"}
      linkHref={linkPath}
      activePattern={pathPattern}
      isOpen={isOpen}
    >
      {children}
    </DragOverlayProject>
  );
}
